import RoundedRect from "Phaser/GameObjects/RoundedRect";
import Phaser from "phaser";
import { colors } from "Phaser/config";
import {
  NotifyPhaser,
  NotifyReact,
  PulseNote,
  ReactToExerciseEventType,
  RemoveListener,
  SetNoteState,
  SetPhraseEligible,
  ShowFeedbackToast,
} from "Phaser/EventBus";
import {
  MidiEventState,
  PrerenderedGraphics,
  PrerenderedPhraseData,
} from "Types";
import EventStream, { NoteEvent } from "Models/EventStream";
import { INoteGraphicsDatabase } from "Phaser/Scenes/StaffExercise";
import Phrase, { Iterator } from "Models/Phrase";
import { NoteType } from "@museflow-ai/opensheetmusicdisplay";
import { ProgressState } from "./Progress";
import TimeSignature from "Models/TimeSignature";
import { COLORBLIND_TYPE } from "Utils/Constants";
import { ErrorFeedbackManager } from "./ErrorFeedbackManager";
import SightReadingPhaser from "Phaser/Scenes/SightReadingPhaser";
import { NoteGraphic } from "./StaffLine/Note";

const getPhraseDuration = (cursorData: PrerenderedPhraseData[]): number => {
  if (cursorData.length === 0) {
    return 0;
  }
  let last: PrerenderedPhraseData = cursorData[0];
  for (const slice of cursorData) {
    if (last === null || slice.currentTimestamp > last.currentTimestamp) {
      last = slice;
    }
  }
  const lastOffset = Math.max(
    ...last!.currentVoiceEntries.map(({ Notes }) =>
      Math.max(...Notes.map(({ length }) => length)),
    ),
  );
  return last!.currentTimestamp + lastOffset;
};

/**
 * Interface for iterators that can be used with CursorMobile.
 * Both IteratorExtensible and OSMDIteratorExtensible implement this interface.
 */
export interface ICursorIterator {
  measureTimestamps: number[];
  lastPlayedMeasure: number;
  timestampOffset: number;
  currentTimeStamp: number;
  currentMeasureIndex: number;
  cursorData: PrerenderedPhraseData[];

  // Accessor properties (getters)
  readonly CurrentMeasureIndex: number;
  readonly EndReached: boolean;
  readonly CurrentVoiceEntries: PrerenderedPhraseData["currentVoiceEntries"];

  // Methods
  left(dbg?: boolean): number;
  getNextTimestamp(): number;
  totalDuration(): number;
  lastNoteDuration(): number;
  next(
    tempo?: number,
    onLastSlice?: (
      start: number,
      duration: number,
      durationInSecs: number,
    ) => void,
    ctx?: unknown,
  ): void;
  addPhrase(
    cursorData: PrerenderedPhraseData[],
    timeSig: TimeSignature,
    phraseId: string,
  ): void;
  rollbackToMeasure(measure: number): void;
}

// This will not work when used outside a phaser context
export class IteratorExtensible extends Iterator implements ICursorIterator {
  totalLength: number;
  scene: INoteGraphicsDatabase;
  starts: number[] = [0];
  lastPlayedMeasure: number = 0;
  timestampOffset: number = 0;
  measureTimestamps: number[] = [];
  shouldIncrementMeasureIndex: boolean = false;

  constructor(
    scene: INoteGraphicsDatabase,
    cursorData: PrerenderedPhraseData[],
    phrase: Phrase,
    timeSig: TimeSignature,
    timestampOffset?: number,
    mobile?: boolean,
  ) {
    super(cursorData, phrase);
    this.measureTimestamps = this.cursorData.map(
      (_, idx) => (1 / timeSig.denominator) * timeSig.numerator * idx,
    );
    this.scene = scene;
    this.totalLength = getPhraseDuration(cursorData);
    this.timestampOffset = timestampOffset ?? 0;
  }

  addPhrase(
    cursorData: PrerenderedPhraseData[],
    timeSig: TimeSignature,
    phraseId: string,
  ) {
    this.cursorData.push(
      ...cursorData
        .map((item) => ({
          ...item,
          currentVoiceEntries: item.currentVoiceEntries.filter(
            (entry) =>
              entry.Notes.filter((note) => note.printObject).length > 0,
          ),
          currentTimestamp: item.currentTimestamp + this.totalLength,
        }))
        .filter(({ currentVoiceEntries }) => currentVoiceEntries.length > 0),
    );
    const lastMeasure = this.measureTimestamps.length;
    this.measureTimestamps.push(
      ...cursorData.map(
        (_, idx) =>
          (1 / timeSig.denominator) * timeSig.numerator * (idx + lastMeasure),
      ),
    );
    this.totalLength += getPhraseDuration(cursorData);
    this.endReached = false;
  }

  lastNoteDuration() {
    return (
      this.totalLength -
      this.cursorData[this.cursorData.length - 1].currentTimestamp
    );
  }

  getPhraseEndPosition(): number | undefined {
    return this.scene.getPhraseEndPosition();
  }

  getGraphicsLeft(currentNoteId: string, rest?: boolean): number | undefined {
    const pos = this.scene.getNotePosition(
      currentNoteId,
      this.currentTimeStamp + this.timestampOffset,
      rest,
    );
    return pos ?? undefined;
  }

  totalDuration() {
    return this.totalLength;
  }

  getLastPlayedMeasure(): number {
    return this.lastPlayedMeasure;
  }

  left(dbg?: boolean): number {
    try {
      if (this.EndReached) {
        return this.getPhraseEndPosition()!;
      } else {
        const notes = this.CurrentVoiceEntries.flatMap((entry) =>
          entry.Notes.filter((note) => !note.isRest).map(
            (note) => this.getGraphicsLeft(note.graphics[0].staveNote.id)!,
          ),
        );
        const positions =
          notes.length !== 0
            ? notes
            : this.CurrentVoiceEntries.flatMap((entry) =>
                entry.Notes.filter((note) => note.printObject).map((note) => {
                  const pos = this.getGraphicsLeft(
                    note.graphics[0].staveNote.id,
                    note.isRest,
                  )!;
                  return pos;
                }),
              );
        return Math.min(...positions);
      }
    } catch (error) {
      console.error(
        "Error when getting the x-position of the next note to play.",
        error,
      );
      return 0;
    }
  }

  getNextTimestamp(): number {
    if (this.EndReached) {
      return this.totalLength;
    } else return this.currentTimeStamp;
  }

  next(
    tempo?: number,
    onLastSlice?: (
      start: number,
      duration: number,
      durationInSecs: number,
    ) => void,
    ctx?: any,
  ) {
    if (this.indx >= this.cursorData.length) {
      console.log("length:", this.cursorData.length, this.indx);
    }
    super.next();
    if (this.shouldIncrementMeasureIndex) {
      this.lastPlayedMeasure++;
      this.shouldIncrementMeasureIndex = false;
    } else if (this.currentMeasureIndex > this.lastPlayedMeasure) {
      this.starts[this.currentMeasureIndex] = this.indx;
      this.shouldIncrementMeasureIndex = true;
    }
    //console.log('length', this.indx, this.cursorData.length, this.EndReached);
    if (tempo) {
      const durationInSecs =
        (this.totalDuration() - this.currentTimeStamp) * (60 / tempo) * 4;
      if (this.endReached) {
        onLastSlice?.bind(ctx)(
          this.currentTimeStamp,
          this.totalDuration() - this.currentTimeStamp,
          durationInSecs,
        );
      }
    }
  }

  rollbackToMeasure(measure: number) {
    this.endReached = false;
    this.indx = this.starts[measure] - 1;
    this.next(0);
  }
}

export class EventStreamPhaser2 extends EventStream {
  private errorFeedbackManager?: ErrorFeedbackManager;
  private scene?: SightReadingPhaser;

  /**
   * Set the error feedback manager for spawning visual feedback.
   */
  setErrorFeedbackManager(
    manager: ErrorFeedbackManager,
    scene: SightReadingPhaser,
  ): void {
    this.errorFeedbackManager = manager;
    this.scene = scene;
  }

  protected markOnsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number,
    pulse: boolean = false,
    midiNote: number | null,
  ) {
    // Always call super first to ensure base functionality (note coloring, pulses, etc.) works
    super.markOnsetAccuracyWithSideEffects(
      note,
      eventState,
      timestamp,
      pulse,
      midiNote,
    );

    // Then handle additional visual feedback for errors
    switch (eventState) {
      case MidiEventState.EARLY_ONSET:
      case MidiEventState.LATE_ONSET:
      case MidiEventState.WRONG_NOTE:
        NotifyReact(ShowFeedbackToast(eventState));
        // Spawn visual feedback if manager is available
        if (this.errorFeedbackManager && this.scene) {
          this.spawnOnsetErrorFeedback(note, eventState, midiNote);
        }
        break;
    }
  }

  protected markOffsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number | null,
  ) {
    // Always call super first to ensure base functionality (note coloring, etc.) works
    super.markOffsetAccuracyWithSideEffects(note, eventState, timestamp);

    // Then handle additional visual feedback for errors
    switch (eventState) {
      case MidiEventState.EARLY_OFFSET:
      case MidiEventState.LATE_OFFSET:
        NotifyReact(ShowFeedbackToast(eventState));
        // Spawn visual feedback if manager is available
        if (this.errorFeedbackManager && this.scene) {
          this.spawnOffsetErrorFeedback(note, eventState);
        }
        break;
    }
  }

  /**
   * Spawn visual feedback for onset errors (early/late/wrong note).
   */
  private spawnOnsetErrorFeedback(
    note: NoteEvent,
    eventState: MidiEventState,
    midiNote: number | null,
  ): void {
    if (!this.scene || !this.errorFeedbackManager) return;

    const activePhrase = this.scene.phrases[this.scene.getActivePhrase()];
    if (!activePhrase || !activePhrase.cursor) return;

    const cursorWorldX = activePhrase.cursor.x;
    const phraseGraphic = activePhrase.graphic;
    const noteId = note.Graphics[0].staveNote.id;
    const originalNote = phraseGraphic.getNoteById(noteId);

    if (!originalNote) {
      console.warn("Could not find original note for error feedback", noteId);
      return;
    }

    // Get note length from original note
    const noteLength = (originalNote as NoteGraphic).noteLength ?? 0.25;

    // Get colorblind type from EventStream
    const colorblindType = (this as any)
      .colorblindType as COLORBLIND_TYPE | null;

    // Track note-on position for offset bar calculation
    const phraseUuid = phraseGraphic._data.uuid;
    this.errorFeedbackManager.setNoteOnPosition(
      phraseUuid,
      noteId,
      cursorWorldX,
    );

    // Spawn phantom note
    const phantomNote = phraseGraphic.spawnPhantomNote(
      cursorWorldX,
      eventState,
      noteId,
      midiNote,
      noteLength,
      colorblindType,
    );

    if (phantomNote) {
      this.errorFeedbackManager.addPhantomNote(phraseUuid, phantomNote, noteId);
    }
  }

  /**
   * Spawn visual feedback for offset errors (early/late release).
   */
  private spawnOffsetErrorFeedback(
    note: NoteEvent,
    eventState: MidiEventState,
  ): void {
    if (!this.scene || !this.errorFeedbackManager) return;

    const activePhrase = this.scene.phrases[this.scene.getActivePhrase()];
    if (!activePhrase || !activePhrase.cursor) return;

    const cursorWorldX = activePhrase.cursor.x;
    const phraseGraphic = activePhrase.graphic;
    const noteId = note.Graphics[0].staveNote.id;
    const originalNote = phraseGraphic.getNoteById(noteId);

    if (!originalNote) {
      console.warn(
        "Could not find original note for offset error feedback",
        noteId,
      );
      return;
    }

    const phraseUuid = phraseGraphic._data.uuid;
    const noteOnX = this.errorFeedbackManager.getNoteOnPosition(
      phraseUuid,
      noteId,
    );

    if (noteOnX === undefined) {
      console.warn(
        "Could not find note-on position for offset error feedback",
        noteId,
      );
      return;
    }

    // Get the phantom note if it exists (for combined errors)
    const phantomNote = this.errorFeedbackManager.getPhantomNote(
      phraseUuid,
      noteId,
    );

    // Determine which clef the note belongs to
    const trebleNote = phraseGraphic.trebleClef?.getNote(noteId);
    const clef: "treble" | "bass" = trebleNote ? "treble" : "bass";

    // Get note y position (in clef local coordinates)
    // Use the note's line number to calculate y position relative to clef
    const noteLine = (originalNote as NoteGraphic).line;
    const noteY = (noteLine / 2) * phraseGraphic.config.pitchLineGap;

    // Get colorblind type
    const colorblindType = (this as any)
      .colorblindType as COLORBLIND_TYPE | null;

    // Spawn duration bar
    const bar = phraseGraphic.spawnPhantomBar(
      noteOnX,
      cursorWorldX,
      noteY,
      clef,
      colorblindType,
    );

    if (bar) {
      this.errorFeedbackManager.addPhantomBar(phraseUuid, bar);
    }
  }

  pulseMatchedNote(
    note: NoteEvent,
    _colorClass: string,
    noteTimestamp?: number,
  ) {
    const colorblindType = (this as any)
      .colorblindType as COLORBLIND_TYPE | null;
    NotifyPhaser(
      PulseNote(note.Graphics[0].staveNote.id, colorblindType, noteTimestamp),
    );
  }

  makeNoteSemiError = (
    graphics: PrerenderedGraphics[],
    cbType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => {
    console.log("semi error");
    for (const graphic of graphics) {
      NotifyPhaser(
        SetNoteState(
          graphic.staveNote.id,
          ProgressState.LateStart,
          cbType,
          noteTimestamp,
        ),
      );
    }
  };

  makeNoteCorrect = (
    graphics: PrerenderedGraphics[],
    cbType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => {
    console.log("correct");
    for (const graphic of graphics) {
      NotifyPhaser(
        SetNoteState(
          graphic.staveNote.id,
          ProgressState.Ended,
          cbType,
          noteTimestamp,
        ),
      );
    }
  };

  makeNoteError = (
    graphics: PrerenderedGraphics[],
    cbType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => {
    console.log("error");
    for (const graphic of graphics) {
      NotifyPhaser(
        SetNoteState(
          graphic.staveNote.id,
          ProgressState.Missed,
          cbType,
          noteTimestamp,
        ),
      );
    }
  };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (a: number, min: number, max: number) =>
  a < min ? min : a > max ? max : a;

const cursorPaddingLeft = 200 * 1;

export default class CursorMobile extends RoundedRect {
  private lastExpectedOnset: number = -1;
  private timestampOffset?: number;
  private relPos: number = 0;
  private lastNotePos: number = 0;
  private midiStream: EventStreamPhaser2;
  private tempo: number = 0;
  private iterator: ICursorIterator;
  private cameraControl: boolean = false;

  private running: boolean = false;

  private blinkTween?: Phaser.Tweens.Tween;
  private fadeTween?: Phaser.Tweens.Tween;
  private origAlpha: number;

  private eligible: boolean = false;
  private firstPhrase: boolean = false;

  private onLastSlice?: (
    start: number,
    duration: number,
    durationInSecs: number,
  ) => void;
  private onFinishPlaying?: (eligible?: boolean) => void;
  private finishCbScope?: any;
  idx: number = 0;
  startingMeasure: number = 0;

  flag = true;
  countInDuration?: number;
  enabled = false;

  timer?: Phaser.Time.TimerEvent;
  lastPlayedMeasure?: number;

  waitForTimerReset: boolean = false;
  playedOnce: boolean = false;

  cursorData: PrerenderedPhraseData[] = [];

  constructor(
    scene: INoteGraphicsDatabase & Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    midiStream: EventStreamPhaser2,
    iterator: ICursorIterator,
    cursorData: PrerenderedPhraseData[],
    cameraControl?: boolean,
    onLastSlice?: (
      start: number,
      duration: number,
      durationInSecs: number,
    ) => void,
    onFinishPlaying?: (eligible?: boolean) => void,
    cbScope?: any,
    hidden?: boolean,
    timestampOffset?: number,
    idx?: number,
    firstPhrase?: boolean,
  ) {
    super(
      scene,
      x,
      y,
      width,
      height,
      width / 2,
      colors.cursor,
      colors.cursorAlpha,
    );
    this.cursorData = cursorData;
    this.firstPhrase = firstPhrase ?? false;
    this.idx = idx ?? 0;
    this.lastNotePos = x;
    this.timestampOffset = timestampOffset;
    this.origAlpha = colors.cursorAlpha;
    this.iterator = iterator;
    this.midiStream = midiStream;
    this.tempo = midiStream.timeKeeper.getBpm();
    this.scene = scene;
    this.onLastSlice = onLastSlice;
    this.onFinishPlaying = onFinishPlaying;
    this.finishCbScope = cbScope;
    // The code for this cursor will only be used in a "Lesson" context
    // and will therefore not have an "Exercise" associated with it.
    // Hence, we handle all pause / resume related events directly in
    // the context of the cursor itself as it is the only scope where
    // gameplay related logic is implemented within the parent phaser
    // scene.
    this.cameraControl = cameraControl ?? false;
    this.setX(x - width / 2);
    if (this.cameraControl) {
      this.scene.cameras.main.scrollX =
        this.x + this.width / 2 - cursorPaddingLeft;
      //this.scene.cameras.main.scrollY = -300;
    }
    if (hidden) this.setAlpha(0);
  }

  disable() {
    this.running = false;
    this.enabled = false;
    this.setAlpha(0);
  }

  enable() {
    //this.running = true;
    this.setAlpha(colors.cursorAlpha);
    this.enabled = true;
    //console.debug
  }

  isFirstPhrase() {
    return this.firstPhrase;
  }

  setEligible(eligible: boolean) {
    if (this.firstPhrase || eligible === this.eligible) return;
    this.eligible = eligible;
    NotifyReact(SetPhraseEligible(eligible));
  }

  getLastPlayedMeasure(): number {
    /*const timeStamp = this.getTimestamp();
    const measureTimestamp = this.iterator.measureTimestamps[this.iterator.CurrentMeasureIndex] + (this.timestampOffset ?? 0);
    let lastMeasure;
    if (timeStamp < measureTimestamp) {
      console.debug('midway', timeStamp);
      lastMeasure = this.startingMeasure + this.iterator.CurrentMeasureIndex - 2;
    }
    else {
      lastMeasure = this.startingMeasure + this.iterator.CurrentMeasureIndex - 1;
    }
    if (this.lastPlayedMeasure === undefined || this.lastPlayedMeasure < lastMeasure) this.lastPlayedMeasure = lastMeasure;
    return this.lastPlayedMeasure;*/
    const lastMeasure = this.startingMeasure + this.iterator.lastPlayedMeasure;
    if (
      this.lastPlayedMeasure === undefined ||
      lastMeasure > this.lastPlayedMeasure
    ) {
      this.lastPlayedMeasure = lastMeasure;
    }
    return this.lastPlayedMeasure;
    //return this.startingMeasure + this.iterator.CurrentMeasureIndex - 1;
  }

  setLastNotePos(x: number) {
    this.lastNotePos = x;
  }

  startCountingIn(startTimestamp: number, lastOnset: number, duration: number) {
    this.enabled = true;
    this.lastNotePos = this.x;
    this.countInDuration = duration;
    this.lastExpectedOnset = -lastOnset;
  }

  getTimestamp() {
    return this.midiStream.timeKeeper.audioTimeToMeasureTimestamp();
  }

  calcPos() {
    const nextXPosition = this.iterator.left(
      //this.countInDuration !== undefined
      true,
    );
    //const YPos = this.iterator.getGraphicsY();
    const nextTimestamp = this.iterator.getNextTimestamp();
    const timeDiff = nextTimestamp - this.lastExpectedOnset;
    const currentTimestamp =
      this.midiStream.timeKeeper.audioTimeToMeasureTimestamp() -
      (this.timestampOffset ?? 0);
    let prog = currentTimestamp - this.lastExpectedOnset;
    if (this.countInDuration) {
      prog = currentTimestamp + this.countInDuration;
    }
    let t = timeDiff ? prog / timeDiff : 1;
    if (this.countInDuration) t = clamp(Math.pow(t, 4), 0, 1);
    const x = lerp(this.lastNotePos, nextXPosition, t);
    return x;
  }

  update() {
    if (!this.enabled) return;
    let currentTimestamp =
      this.midiStream.timeKeeper.audioTimeToMeasureTimestamp() -
      (this.timestampOffset ?? 0);
    if (this.countInDuration !== undefined) {
      this.relPos = this.calcPos();
      this.moveTo(this.relPos);
      let amt = clamp(
        (currentTimestamp - this.lastExpectedOnset) / this.countInDuration,
        0,
        1,
      );
      if (this.countInDuration) amt = Math.pow(amt, 4);
      const alpha = lerp(0, this.origAlpha, amt);
      if (amt === 1) this.countInDuration = undefined;
      this.setAlpha(alpha);
    }
    if (
      this.running &&
      currentTimestamp >= this.iterator.totalDuration() &&
      !this.waitForTimerReset
    ) {
      this.onFinishPlaying?.call(
        this.finishCbScope,
        this.eligible && !this.playedOnce,
      );
      this.playedOnce = true;
      this.running = false;
    }
    if (
      !this.running ||
      this.midiStream.timeKeeper.isPaused() ||
      this.midiStream.timeKeeper.audioTimeToMeasureTimestamp() >=
        this.iterator.totalDuration() + this.iterator.timestampOffset
    )
      return;
    const bpm = this.midiStream.timeKeeper.getBpm();
    if (this.tempo !== bpm) {
      this.tempo = bpm;
    }
    //this.calcPos();
    this.relPos = this.calcPos();
    this.flag = false;
    this.moveTo(this.relPos);
    // Move on to the next slice when we've reached our target
    //if (this.relPos >= this.iterator.left()) {
    if (this.iterator.EndReached) {
      const lastNoteDuration = this.iterator.lastNoteDuration();
      const amt =
        (this.iterator.totalDuration() - currentTimestamp) / lastNoteDuration;
      const alpha = lerp(this.origAlpha, 0, Math.pow(1 - amt, 4));
      this.setAlpha(alpha);
    }
    const comp = this.iterator.EndReached
      ? this.iterator.totalDuration()
      : this.iterator.currentTimeStamp;
    if (this.running && currentTimestamp >= comp) {
      this.lastExpectedOnset = this.iterator.currentTimeStamp;
      this.lastNotePos = this.relPos;
      this.iterator.next(
        this.midiStream.timeKeeper.getBpm(),
        this.onLastSlice,
        this.finishCbScope,
      );
    }
  }

  moveTo(pos: number) {
    //console.log(pos);
    //this.scene.cameras.main.setPosition(-pos, this.scene.cameras.main.y);
    if (this.cameraControl)
      this.scene.cameras.main.scrollX = pos - cursorPaddingLeft;
    this.setX(pos - this.width / 2);
  }

  reset(
    iterator: ICursorIterator,
    timestampOffset: number,
    measureOffset: number,
  ) {
    this.startingMeasure = measureOffset;
    this.iterator = iterator;
    //this.clearBlink();
    this.running = false;
    this.lastNotePos = 0;
    this.lastExpectedOnset = -1;
    this.relPos = 0;
    this.timestampOffset = timestampOffset;
    this.iterator.timestampOffset = timestampOffset;
  }

  addPhrase(
    cursorData: PrerenderedPhraseData[],
    id: string,
    timeSig: TimeSignature,
  ) {
    this.iterator.addPhrase(cursorData, timeSig, id);
  }

  unpause(inaccComp?: number) {
    this.waitForTimerReset = true;
    this.timer?.remove();
    this.timer = this.scene.time.addEvent({
      callback: () => {
        this.waitForTimerReset = false;
      },
      delay: 50,
      callbackScope: this,
    });
    this.clearBlink();
    this.calcPos();
    this.running = true;
    this.countInDuration = undefined;
  }

  pause() {
    this.running = false;
    this.timer?.remove();
    this.timer = undefined;
    this.clearBlink();
  }

  setStart(lastNote: number, timestamp: number, measureOffset?: number) {
    this.iterator.rollbackToMeasure(measureOffset ?? 0);
    this.lastNotePos = lastNote;
    this.lastExpectedOnset = timestamp;
    this.moveTo(lastNote);
  }

  blink(duration: number) {
    if (!this.enabled) return;
    this.clearBlink();
    this.blinkTween = this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration,
      onUpdate: this.draw,
      callbackScope: this,
    });
  }

  clearBlink(alpha: number = colors.cursorAlpha) {
    this.blinkTween?.stop();
    this.setAlpha(alpha);
  }

  destroy(removeListeners?: boolean) {
    super.destroy();
    if (removeListeners) {
      RemoveListener(ReactToExerciseEventType.PauseExercise);
      RemoveListener(ReactToExerciseEventType.ResumeExercise);
    }
  }

  tryStart() {}

  waitForInput(tempo: number) {
    this.midiStream.timeKeeper.unpause(tempo, 0, 0.1, NoteType._32nd);
    //this.running = true;
  }
}
